import { useEffect, useRef, useState } from 'react'
import TitleHeader from '../TitleHeader/TitleHeader'
import leftConnectorIcon from '../../../assets/images/developmentlanguage/autpmationlineleft.svg'
import rightConnectorIcon from '../../../assets/images/developmentlanguage/automationlineright.svg'
import Constants from '../../../app/utils/constants'

const automationItems = Constants.automationItems

// Website URLs — keyed by item.name
const automationLinks: Record<string, string> = {
  Make: 'https://www.make.com',
  Zapier: 'https://zapier.com',
  n8n: 'https://n8n.io',
}

const AutomationService = () => {

  const containerRef = useRef<HTMLElement>(null)
  const blobRef = useRef<HTMLDivElement>(null)
  const [isInside, setIsInside] = useState(false)

  useEffect(() => {
    let clientX = 0, clientY = 0, currentX = 0, currentY = 0, rafId = 0
    let inside = false

    const container = containerRef.current
    if (!container) return

    const getRelativePos = () => {
      const rect = container.getBoundingClientRect()
      return { x: clientX - rect.left, y: clientY - rect.top }
    }
    const setPos = (x: number, y: number) => {
      if (blobRef.current)
        blobRef.current.style.transform = `translate(${x}px, ${y}px) translate(-50%, -50%)`
    }
    const handleMouseMove = (e: MouseEvent) => { clientX = e.clientX; clientY = e.clientY }
    const handleEnter = (e: MouseEvent) => {
      clientX = e.clientX; clientY = e.clientY
      const { x, y } = getRelativePos()
      currentX = x; currentY = y; setPos(x, y)
      inside = true; setIsInside(true)
    }
    const handleLeave = () => { inside = false; setIsInside(false) }
    const handleScroll = () => {
      if (!inside) return
      const { x, y } = getRelativePos()
      currentX = x; currentY = y; setPos(x, y)
    }
    const animate = () => {
      if (inside) {
        const { x, y } = getRelativePos()
        currentX += (x - currentX) * 0.1
        currentY += (y - currentY) * 0.1
        setPos(currentX, currentY)
      }
      rafId = requestAnimationFrame(animate)
    }
    container.addEventListener('mousemove', handleMouseMove)
    container.addEventListener('mouseenter', handleEnter)
    container.addEventListener('mouseleave', handleLeave)
    window.addEventListener('scroll', handleScroll, { passive: true })
    rafId = requestAnimationFrame(animate)
    return () => {
      container.removeEventListener('mousemove', handleMouseMove)
      container.removeEventListener('mouseenter', handleEnter)
      container.removeEventListener('mouseleave', handleLeave)
      window.removeEventListener('scroll', handleScroll)
      cancelAnimationFrame(rafId)
    }
  }, [])

  const gridRef = useRef<HTMLDivElement>(null)
  const [gridVisible, setGridVisible] = useState(false)

  useEffect(() => {
    const el = gridRef.current
    if (!el) return
    const obs = new IntersectionObserver(
      ([entry]) => setGridVisible(entry.isIntersecting),
      // threshold 0.85 → nearly the full grid must be on screen
      { threshold: 0.85 },
    )
    obs.observe(el)
    return () => obs.disconnect()
  }, [])

  const DELAY_CENTER = 0
  const DELAY_CONNECTORS = 0.45
  const DELAY_SIDES = 1.2

  return (
    <section
      ref={containerRef}
      className="relative w-full overflow-hidden py-12 md:py-16 px-4 sm:px-6 md:px-10 lg:px-16 xl:px-20 2xl:px-[12%]"
    >
      {/* Cursor glow blob */}
      <div
        ref={blobRef}
        className={`pointer-events-none absolute w-[600px] h-[600px] rounded-full
          bg-[radial-gradient(circle_at_center,#9e5ddfba,transparent_70%)]
          blur-3xl z-0 transition-opacity duration-300 ease-out
          ${isInside ? 'opacity-50' : 'opacity-0'}`}
      />

      <style>{`
        /* ── Center & sides: scale small → full ── */
        .auto-scale-hidden {
          transform: scale(0.3);
          opacity: 0;
          transition: transform 0.55s ease-in-out,
                      opacity 0.45s ease-in-out;
        }
        .auto-scale-show {
          transform: scale(1);
          opacity: 1;
          transition: transform 0.55s ease-in-out,
                      opacity 0.45s ease-in-out;
        }

        /* ── Connector SVGs: clip top → bottom (reveal top-to-bottom) ── */
        .auto-connector-hidden {
          clip-path: inset(0 0 100% 0);
          opacity: 0;
          transition: clip-path 0.75s ease-in-out,
                      opacity 0.4s ease-in-out;
        }
        .auto-connector-show {
          clip-path: inset(0 0 0% 0);
          opacity: 1;
            transition: clip-path 0.75s ease-in-out,
                      opacity 0.4s ease-in-out;
        }

        /* ── Icon float ── */
        @keyframes autoFloat {
          0%, 100% { transform: translateY(0); }
          50%       { transform: translateY(-7px); }
        }
        .auto-icon-float { animation: autoFloat 3s ease-out infinite; }
      `}</style>

      {/* Header */}
      <div className="relative z-10 mb-8 flex justify-center md:mb-10">
        <TitleHeader
          badgeText="Software we are using"
          titleLines={['Our Automation Services']}
          wrapperClassName="flex flex-col items-center text-center"
          titleClassName="font-rajdhani text-3xl font-semibold md:text-4  xl lg:text-4xl xl:text-4xl 2xl:text-6xl"
          badgeClassName="px-8 py-2 text-sm font-rajdhani font-semibold uppercase md:text-lg lg:text-xl xl:text-xl 2xl:text-2xl"
        />
      </div>

      {/* Grid — observed as a whole unit */}
      <div
        ref={gridRef}
        className="relative z-10 mx-auto grid w-full max-w-[1120px]
        grid-cols-[0.9fr_auto_0.9fr_auto_0.9fr]
        sm:grid-cols-[1fr_auto_1fr_auto_1fr]
        grid-rows-[auto_auto] items-start"
      >

        {/* Left connector image — col 2, row 1 — reveals top-to-bottom */}
        <img
          src={rightConnectorIcon}
          alt=""
          aria-hidden
          style={{ transitionDelay: gridVisible ? `${DELAY_CONNECTORS}s` : '0s' }}
          className={`col-start-2 row-start-1 self-end h-auto
            w-25 sm:w-40 md:w-60 lg:w-60 xl:w-80 2xl:w-100
            object-contain
            ${gridVisible ? 'auto-connector-show' : 'auto-connector-hidden'}`}
        />

        {/* Right connector image — col 4, row 1 — reveals top-to-bottom */}
        <img
          src={leftConnectorIcon}
          alt=""
          aria-hidden
          style={{ transitionDelay: gridVisible ? `${DELAY_CONNECTORS}s` : '0s' }}
          className={`col-start-4 row-start-1 self-end h-auto
            w-25 sm:w-40 md:w-60 lg:w-60 xl:w-80 2xl:w-100
            object-contain
            ${gridVisible ? 'auto-connector-show' : 'auto-connector-hidden'}`}
        />

        {/* ── Center item (index 1) — scale small → big FIRST ── */}
        <div
          style={{ transitionDelay: gridVisible ? `${DELAY_CENTER}s` : '0s' }}
          className={`col-start-3 row-start-1 flex flex-col items-center
            ${gridVisible ? 'auto-scale-show' : 'auto-scale-hidden'}`}
        >
          <a
            href={automationLinks[automationItems[1]?.name] ?? '#'}
            target="_blank"
            rel="noopener noreferrer"
            className="group flex flex-col items-center cursor-pointer mb-5"
          >
            <div className="rounded-full p-[1.25px] bg-[linear-gradient(138.94deg,#745EF0_0%,rgba(116,94,240,0.1)_50%,#745EF0_100%)]
              transition-transform duration-300 group-hover:scale-110">
              <div className="flex h-15 w-15 items-center justify-center rounded-full bg-[#0E0924]
                md:h-22 md:w-22 lg:h-20 lg:w-20 xl:h-25 xl:w-25 2xl:h-32 2xl:w-32">
                <img
                  src={automationItems[1]?.icon}
                  alt={automationItems[1]?.name}
                  className="auto-icon-float h-8 w-8 object-contain md:h-12 md:w-12 lg:h-10 lg:w-10 xl:h-12 xl:w-12 2xl:h-16 2xl:w-16"
                />
              </div>
            </div>
            <p className="mt-3 text-center font-gilroy-bold text-base text-white
              md:text-lg lg:text-xl xl:text-2xl 2xl:text-3xl
              transition-colors duration-300 group-hover:text-purple-300 underline-offset-4 group-hover:underline">
              {automationItems[1]?.name}
            </p>
          </a>
        </div>

        {/* ── Left item (index 0) — scale small → big LAST ── */}
        <div
          style={{ transitionDelay: gridVisible ? `${DELAY_SIDES}s` : '0s' }}
          className={`col-start-1 row-start-2 -mt-15 flex flex-col items-center
            ${gridVisible ? 'auto-scale-show' : 'auto-scale-hidden'}`}
        >
          <a
            href={automationLinks[automationItems[0]?.name] ?? '#'}
            target="_blank"
            rel="noopener noreferrer"
            className="group flex flex-col items-center cursor-pointer"
          >
            <div className="rounded-full p-[1.25px] bg-[linear-gradient(138.94deg,#745EF0_0%,rgba(116,94,240,0.1)_50%,#745EF0_100%)]
              transition-transform duration-300 group-hover:scale-110">
              <div className="flex h-15 w-15 items-center justify-center rounded-full bg-[#0E0924]
                md:h-22 md:w-22 lg:h-20 lg:w-20 xl:h-25 xl:w-25 2xl:h-32 2xl:w-32">
                <img
                  src={automationItems[0]?.icon}
                  alt={automationItems[0]?.name}
                  className="auto-icon-float h-8 w-8 object-contain md:h-12 md:w-12 lg:h-10 lg:w-10 xl:h-12 xl:w-12 2xl:h-16 2xl:w-16"
                />
              </div>
            </div>
            <p className="mt-3 text-center font-gilroy-bold text-base text-white
              md:text-lg lg:text-xl xl:text-2xl 2xl:text-3xl
              transition-colors duration-300 group-hover:text-purple-300 underline-offset-4 group-hover:underline">
              {automationItems[0]?.name}
            </p>
          </a>
        </div>

        {/* ── Right item (index 2) — scale small → big LAST (same time as left) ── */}
        <div
          style={{ transitionDelay: gridVisible ? `${DELAY_SIDES}s` : '0s' }}
          className={`col-start-5 row-start-2 -mt-15 flex flex-col items-center
            ${gridVisible ? 'auto-scale-show' : 'auto-scale-hidden'}`}
        >
          <a
            href={automationLinks[automationItems[2]?.name] ?? '#'}
            target="_blank"
            rel="noopener noreferrer"
            className="group flex flex-col items-center cursor-pointer"
          >
            <div className="rounded-full p-[1.25px] bg-[linear-gradient(138.94deg,#745EF0_0%,rgba(116,94,240,0.1)_50%,#745EF0_100%)]
              transition-transform duration-300 group-hover:scale-110">
              <div className="flex h-15 w-15 items-center justify-center rounded-full bg-[#0E0924]
                md:h-22 md:w-22 lg:h-20 lg:w-20 xl:h-25 xl:w-25 2xl:h-32 2xl:w-32">
                <img
                  src={automationItems[2]?.icon}
                  alt={automationItems[2]?.name}
                  className="auto-icon-float h-8 w-8 object-contain md:h-12 md:w-12 lg:h-10 lg:w-10 xl:h-12 xl:w-12 2xl:h-16 2xl:w-16"
                />
              </div>
            </div>
            <p className="mt-3 text-center font-gilroy-bold text-base text-white
              md:text-lg lg:text-xl xl:text-2xl 2xl:text-3xl
              transition-colors duration-300 group-hover:text-purple-300 underline-offset-4 group-hover:underline">
              {automationItems[2]?.name}
            </p>
          </a>
        </div>

      </div>
    </section>
  )
}

export default AutomationService